import { CheckCircle2, Download, Printer, Send, ShieldCheck, UploadCloud } from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SaveAndResumeLater, type SaveDraftResult } from "../../components/forms/SaveAndResumeLater";
import { buildSubmissionSummary, type SubmissionSummary } from "../../lib/formSubmissionSummary";
import {
  appendStoredDraftFiles,
  buildResumeUrl,
  getCurrentSectionId,
  getResumeDraftId,
  loadFormProgress,
  restoreFormProgress,
  saveFormProgress,
  type DraftFileMeta,
} from "../../lib/formProgress";
import { FormPortalHeader } from "./FormPortalHeader";
import { memoryHubSections, memoryHubSummarySections } from "./formsContent";

type FormErrors = Record<string, string>;

const MEMORY_FORM_KEY = "memory-hub";
const MEMORY_FORM_PATH = "/memory-hub";
const MEMORY_FORM_TITLE = "Memory Hub Form";

const requiredTextFields = ["fullName", "email", "phone", "eventType", "eventName", "eventDate", "memoryHubTitle"] as const;
const requiredRadioFields = ["memoryHubFor", "memoryHubType"] as const;

const requiredCheckboxFields = [
  "accurateConfirmation",
  "contentPermission",
  "serviceUseConfirmation",
  "changeRequestAcknowledgement",
] as const;

const requiredLabels: Record<string, string> = {
  fullName: "Full Name is required.",
  email: "Email Address is required.",
  phone: "Phone Number is required.",
  eventType: "Event Type is required.",
  eventName: "Event Name or Honoree Name is required.",
  eventDate: "Event Date is required.",
  memoryHubFor: "Please select who this Memory Hub is for.",
  memoryHubForOther: "Please describe who this Memory Hub is for.",
  memoryHubType: "Please select a Memory Hub Type.",
  memoryHubTypeOther: "Please describe your Memory Hub type.",
  memoryHubTitle: "Memory Hub Title is required.",
  memoryHubPhotos: "Please upload at least one photo or video before submitting.",
  accurateConfirmation: "Please confirm that the information provided is accurate.",
  contentPermission: "Please confirm that you have permission to submit and use the provided content and files.",
  serviceUseConfirmation: "Please confirm that Kreatethedate may use these details to build your Memory Hub.",
  changeRequestAcknowledgement: "Please confirm that you understand major setup changes may require additional time and/or fees.",
};

const contactOptions = ["Email", "Phone", "Text"];
const timingOptions = ["Past Event", "Upcoming Event", "Not sure yet"];
const memoryHubForOptions = ["Myself", "My Child", "My Family", "A Loved One", "An Organization", "A Client", "Other"];
const memoryHubTypeOptions = ["Personal Event", "Memorial / Celebration of Life", "Business or Organization Event", "Family Legacy", "Other"];
const memoryHubFeelOptions = ["Elegant", "Fun", "Emotional", "Modern", "Luxury", "Spiritual", "Family-Focused", "Other"];
const mediaUseOptions = [
  "Use any media you feel works best.",
  "Use only the media I specifically identify.",
  "Use all submitted media where appropriate.",
  "I would like to discuss media placement during my kick-off call.",
];
const preferenceOptions = ["Yes", "No", "Not sure yet"];
const mediaContributorOptions = ["Only me", "Family members", "Event guests", "Multiple contributors", "Not sure yet"];
const guestMessageCollectedOptions = ["Yes", "No", "I need guests to submit them later"];
const galleryDisplayOptions = ["Simple Gallery", "Story Style Layout", "Timeline Style Layout", "Featured Moments", "Let Kreatethedate Decide"];
const captionsOptions = ["Yes", "No", "Some photos only"];
const sharingPreferenceOptions = [
  "Private shareable link \u2014 Kreatethedate sends the link to me, and I share it with others.",
  "Public shareable link \u2014 anyone with the link can view it.",
  "Password-protected link (if available).",
  "Not sure yet.",
];
const memoryMediaVaultInterestOptions = [
  "Yes, tell me more about the Memory Media Vault.",
  "No, not at this time.",
  "Not sure yet.",
];
const futureServicesOptions = [
  "RSVP Hub",
  "Archive Hub",
  "Memory Media Vault",
  "Signature Song",
  "Signature Highlight Reel",
  "Not at this time",
];

export function MemoryHubFormPage() {
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedSummary, setSubmittedSummary] = useState<SubmissionSummary | null>(null);
  const [draftId, setDraftId] = useState("");
  const [restoreNotice, setRestoreNotice] = useState("");
  const [restoredFiles, setRestoredFiles] = useState<DraftFileMeta[]>([]);
  const [showMemoryHubForOther, setShowMemoryHubForOther] = useState(false);
  const [showMemoryHubTypeOther, setShowMemoryHubTypeOther] = useState(false);
  const [showMemoryHubFeelOther, setShowMemoryHubFeelOther] = useState(false);
  const [showMediaContributors, setShowMediaContributors] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    document.title = "Memory Hub Form | Kreatethedate";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", "Complete the Kreatethedate Memory Hub Form with event details, photos, videos, memories, preferences, and confirmations.");
  }, []);

  useEffect(() => {
    const resumeDraftId = getResumeDraftId();
    if (!resumeDraftId) {
      const targetSectionId = window.location.hash.replace("#", "");
      if (targetSectionId) {
        window.setTimeout(() => {
          document.getElementById(targetSectionId)?.scrollIntoView({ block: "start" });
        }, 80);
      } else {
        window.scrollTo({ top: 0, left: 0 });
      }
      return;
    }

    const draft = loadFormProgress(MEMORY_FORM_KEY, resumeDraftId);
    if (!draft || !formRef.current) {
      setRestoreNotice("We could not find saved progress for this resume link in this browser.");
      return;
    }

    restoreFormProgress(formRef.current, draft);
    setDraftId(draft.draftId);
    setRestoredFiles(draft.fileMeta);
    setRestoreNotice("Your saved progress has been restored in this browser.");
    setShowMemoryHubForOther(draft.fields.memoryHubFor === "Other");
    setShowMemoryHubTypeOther(draft.fields.memoryHubType === "Other");
    setShowMemoryHubFeelOther(Array.isArray(draft.fields.memoryHubFeel) && draft.fields.memoryHubFeel.includes("Other"));
    setShowMediaContributors(draft.fields.additionalMediaLater === "Yes");

    const targetSectionId = window.location.hash.replace("#", "") || draft.lastSectionId || memoryHubSections[0]?.id;
    window.setTimeout(() => {
      if (formRef.current) restoreFormProgress(formRef.current, draft);
      document.getElementById(targetSectionId)?.scrollIntoView({ block: "start" });
    }, 80);
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    formData.set("formType", "memory-hub");
    formData.set("cf-turnstile-response", "turnstile-placeholder");
    if (draftId) await appendStoredDraftFiles(formData, MEMORY_FORM_KEY, draftId);

    const nextErrors = validateMemoryHubForm(formData);
    setErrors(nextErrors);

    const firstInvalidName = Object.keys(nextErrors)[0];
    if (firstInvalidName) {
      setSubmitError("Please complete all required fields before submitting your Memory Hub Form.");
      form.querySelector<HTMLElement>(`[name="${firstInvalidName}"]`)?.focus();
      return;
    }

    const summary = buildSubmissionSummary({
      formData,
      formTitle: "Memory Hub Setup Form",
      clientNameField: "fullName",
      eventNameField: "eventName",
      eventDateField: "eventDate",
      sections: memoryHubSummarySections,
    });

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/forms/memory-hub", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const message = await response.text();
        if (!(import.meta.env.DEV && response.status === 404)) {
          throw new Error(message || "Your form could not be submitted. Please try again.");
        }
      }

      const contentType = response.headers.get("Content-Type") || "";
      if (response.ok && !contentType.includes("application/json") && !import.meta.env.DEV) {
        throw new Error("The Memory Hub submission endpoint is not configured yet.");
      }

      setSubmittedSummary(summary);
      setSubmitted(true);
      formRef.current?.reset();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Your form could not be submitted. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handlePrintCompletedForm() {
    printSubmittedForm(submittedSummary);
  }

  function handleDownloadCompletedForm() {
    printSubmittedForm(submittedSummary);
  }

  async function handleSaveProgress(): Promise<SaveDraftResult> {
    if (!formRef.current) throw new Error("The form is not ready yet.");

    const lastSectionId = getCurrentSectionId(memoryHubSections.map((section) => section.id));
    const draft = await saveFormProgress({
      form: formRef.current,
      formKey: MEMORY_FORM_KEY,
      draftId,
      lastSectionId,
    });
    const resumeLink = buildResumeUrl(MEMORY_FORM_PATH, draft.draftId, lastSectionId);

    setDraftId(draft.draftId);
    setRestoredFiles(draft.fileMeta);
    return { draft, resumeLink };
  }

  return (
    <main className="min-h-screen bg-[#fff9ef] text-[#16120a]">
      <a
        href="#memory-hub-form"
        className="focus-ring sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-[#050505] focus:px-4 focus:py-3 focus:text-[#FF6A2B]"
      >
        Skip to Memory Hub form
      </a>
      <FormPortalHeader />

      <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18">
        <img src="/assets/celebration-table.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,5,5,.94),rgba(5,5,5,.74)_54%,rgba(5,5,5,.48)),linear-gradient(0deg,rgba(5,5,5,.42),rgba(5,5,5,.14))]" />
        <div className="relative mx-auto grid min-h-[46dvh] max-w-7xl items-end gap-8 px-4 pb-10 pt-24 text-[#fff9ef] sm:px-6 lg:grid-cols-[1fr_.72fr] lg:px-8 lg:pb-14">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.24em] text-[#FF6A2B]">Memory Hub Form</p>
            <h1 className="mt-5 max-w-4xl text-balance font-display text-5xl font-bold leading-[0.98] sm:text-6xl">
              Let&apos;s Build Your Memory Hub
            </h1>
          </div>
          <p className="max-w-xl border-l-2 border-[#FF6A2B] pl-4 text-sm font-semibold leading-7 text-[#fff9ef]/76 lg:ml-auto">
            Share your event details, photos, videos, memories, and preferences so we can create a beautiful Memory Hub
            experience for you and your guests.
          </p>
        </div>
      </section>

      {submitted ? (
        <ThankYouState
          onDownloadPdf={handleDownloadCompletedForm}
          onPrint={handlePrintCompletedForm}
          summary={submittedSummary}
        />
      ) : (
        <section className="px-4 pb-28 pt-10 sm:px-6 lg:px-8 lg:pb-28 lg:pt-14">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[290px_1fr]">
            <aside className="hidden h-fit rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] lg:sticky lg:top-24 lg:block">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#D94A16]">Form sections</p>
              <ol className="mt-4 grid gap-2">
                {memoryHubSections.map((section, index) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="focus-ring group grid grid-cols-[34px_1fr] items-center gap-3 rounded-lg px-2 py-2 text-sm font-bold text-[#5c5246] transition hover:bg-[#fff9ef] hover:text-[#050505]"
                    >
                      <span className="grid size-8 place-items-center rounded-full border border-[#FF6A2B]/45 bg-[#050505] text-xs text-[#FF6A2B]">
                        {index + 1}
                      </span>
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </aside>

            <form id="memory-hub-form" ref={formRef} noValidate onSubmit={handleSubmit} className="grid gap-6">
              <SaveAndResumeLater
                formTitle={MEMORY_FORM_TITLE}
                getDefaultEmail={() => {
                  const emailControl = formRef.current?.elements.namedItem("email");
                  return emailControl instanceof HTMLInputElement ? emailControl.value : "";
                }}
                onSaveDraft={handleSaveProgress}
                restoreNotice={restoreNotice}
                savedFiles={restoredFiles}
              />
              <p className="rounded-lg border border-[#D94A16]/20 bg-white px-5 py-4 text-sm font-semibold leading-7 text-[#5c5246]">
                Estimated completion time: 10&ndash;15 minutes. You can save your progress and return at any time.
              </p>
              <p className="rounded-lg border border-[#D94A16]/20 bg-white px-5 py-4 text-sm font-semibold leading-7 text-[#5c5246]">
                Required fields are marked with <span className="font-black text-[#D94A16]">*</span>.
              </p>
              {submitError ? (
                <p className="rounded-lg border border-red-300 bg-red-50 px-5 py-4 text-sm font-bold text-red-800" role="alert">
                  {submitError}
                </p>
              ) : null}

              <FormSection id="contact" number="01" title="Client Contact Information">
                <div className="grid gap-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <TextField name="fullName" label="Full Name" autoComplete="name" required error={errors.fullName} />
                    <TextField name="email" label="Email Address" type="email" autoComplete="email" required error={errors.email} />
                    <TextField name="phone" label="Phone Number" type="tel" autoComplete="tel" required error={errors.phone} />
                    <SelectField name="preferredContactMethod" label="Preferred Contact Method" options={contactOptions} />
                  </div>
                  <div className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
                    <div>
                      <h3 className="font-display text-2xl font-bold text-[#050505]">Additional Event Contact (Optional)</h3>
                      <p className="mt-2 text-sm font-semibold leading-7 text-[#5c5246]">
                        Provide an additional contact person if someone else is helping coordinate the event.
                      </p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                      <TextField name="additionalContactName" label="Name" autoComplete="name" />
                      <TextField name="additionalContactPhone" label="Phone Number" type="tel" autoComplete="tel" />
                      <TextField name="additionalContactEmail" label="Email Address" type="email" autoComplete="email" />
                    </div>
                  </div>
                </div>
              </FormSection>

              <FormSection id="event-details" number="02" title="Event Details">
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField name="eventType" label="Event Type" required error={errors.eventType} />
                  <TextField name="eventName" label="Event Name or Honoree Name" required error={errors.eventName} />
                  <TextField name="eventDate" label="Event Date" type="date" required error={errors.eventDate} />
                  <TextField name="eventLocation" label="Event Location" />
                  <SelectField name="eventTiming" label="Is this for a past event or upcoming event?" options={timingOptions} />
                  <TextareaField name="eventDescription" label="Short Event Description" className="md:col-span-2" rows={4} />
                  <RadioGroupField
                    name="memoryHubFor"
                    label="Who is this Memory Hub for?"
                    options={memoryHubForOptions}
                    required
                    error={errors.memoryHubFor}
                    className="md:col-span-2"
                    onChange={(value) => setShowMemoryHubForOther(value === "Other")}
                  />
                  {showMemoryHubForOther ? (
                    <TextField
                      name="memoryHubForOther"
                      label="Please describe who this Memory Hub is for."
                      required
                      error={errors.memoryHubForOther}
                      className="md:col-span-2"
                    />
                  ) : null}
                  <RadioGroupField
                    name="memoryHubType"
                    label="Memory Hub Type"
                    options={memoryHubTypeOptions}
                    required
                    error={errors.memoryHubType}
                    className="md:col-span-2"
                    onChange={(value) => setShowMemoryHubTypeOther(value === "Other")}
                  />
                  {showMemoryHubTypeOther ? (
                    <TextField
                      name="memoryHubTypeOther"
                      label="Please describe your Memory Hub type."
                      required
                      error={errors.memoryHubTypeOther}
                      className="md:col-span-2"
                    />
                  ) : null}
                </div>
              </FormSection>

              <FormSection id="memory-hub-details" number="03" title="Memory Hub Details">
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField
                    name="memoryHubTitle"
                    label="What title should appear on the Memory Hub?"
                    required
                    error={errors.memoryHubTitle}
                    className="md:col-span-2"
                  />
                  <TextareaField name="memoryHubIntro" label="What short message or introduction should appear on the Memory Hub?" className="md:col-span-2" rows={4} />
                  <CheckboxGroupField
                    name="memoryHubFeel"
                    label="How would you like your Memory Hub to feel?"
                    options={memoryHubFeelOptions}
                    className="md:col-span-2"
                    onChange={(option, checked) => {
                      if (option === "Other") setShowMemoryHubFeelOther(checked);
                    }}
                  />
                  {showMemoryHubFeelOther ? (
                    <TextField name="memoryHubFeelOther" label="Please describe your preferred style." className="md:col-span-2" />
                  ) : null}
                  <TextareaField name="specialSections" label="Are there any special sections you would like included?" className="md:col-span-2" rows={6} />
                </div>
              </FormSection>

              <FormSection id="photos-videos" number="04" title="Photos & Videos">
                <div className="grid gap-4">
                  <p className="rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
                    Upload the photos and videos you would like Kreatethedate to consider for your Memory Hub. Large files
                    may be requested separately if needed.
                  </p>
                  <div className="rounded-lg border border-[#D94A16]/18 bg-white px-4 py-3">
                    <p className="text-sm font-black text-[#16120a]">
                      Photo or Video Upload <RequiredMark />
                    </p>
                    <p className="mt-1 text-sm font-semibold leading-7 text-[#5c5246]">
                      Please upload at least one photo or video before submitting your Memory Hub Form.
                    </p>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <FileField
                      name="memoryHubPhotos"
                      label="Upload Photos for the Memory Hub"
                      accept=".jpg,.jpeg,.png,.heic,.heif,.webp,image/jpeg,image/png,image/heic,image/heif,image/webp"
                      multiple
                      error={errors.memoryHubPhotos}
                      helperText="Add photos you would like considered for the Memory Hub."
                    />
                    <FileField
                      name="memoryHubVideos"
                      label="Upload Videos for the Memory Hub"
                      accept=".mp4,.mov,video/mp4,video/quicktime"
                      multiple
                      helperText="Add videos you would like considered for the Memory Hub."
                    />
                  </div>
                  <RadioGroupField
                    name="additionalMediaLater"
                    label="Will additional photos or videos be added later?"
                    options={preferenceOptions}
                    onChange={(value) => setShowMediaContributors(value === "Yes")}
                  />
                  {showMediaContributors ? (
                    <CheckboxGroupField name="mediaContributors" label="Who will be adding media?" options={mediaContributorOptions} />
                  ) : null}
                </div>
              </FormSection>

              <FormSection id="guest-memories" number="05" title="Guest Memories & Messages">
                <div className="grid gap-4 md:grid-cols-2">
                  <SelectField name="guestMessagesIncluded" label="Would you like guest messages included?" options={preferenceOptions} />
                  <SelectField name="guestMessagesCollected" label="Do you already have guest messages collected?" options={guestMessageCollectedOptions} />
                </div>
              </FormSection>

              <FormSection id="gallery-display" number="06" title="Gallery & Display Preferences">
                <div className="grid gap-4 md:grid-cols-2">
                  <SelectField name="galleryDisplay" label="How should photos and videos be displayed?" options={galleryDisplayOptions} />
                  <SelectField name="captionsIncluded" label="Would you like captions included?" options={captionsOptions} />
                </div>
              </FormSection>

              <FormSection id="privacy-sharing" number="07" title="Privacy & Sharing">
                <div className="grid gap-4">
                  <RadioGroupField
                    name="memoryHubSharingPreference"
                    label="How would you like your Memory Hub shared?"
                    options={sharingPreferenceOptions}
                  />
                  <TextareaField
                    name="additionalSharingInstructions"
                    label="Additional Sharing Instructions (Optional)"
                    helperText="Tell us anything we should know about who should have access to your Memory Hub."
                    rows={5}
                  />
                </div>
              </FormSection>

              <FormSection id="content-preferences" number="08" title="Memory Hub Content Preferences">
                <div className="grid gap-5">
                  <RadioGroupField
                    name="mediaUsePreference"
                    label="How would you like Kreatethedate to use your submitted photos and videos?"
                    options={mediaUseOptions}
                  />
                  <TextareaField
                    name="additionalMediaInstructions"
                    label="Additional Media Instructions"
                    helperText="Tell us anything important about your photos or videos that Kreatethedate should know when creating your Memory Hub."
                    rows={6}
                  />
                  <RadioGroupField
                    name="memoryMediaVaultInterest"
                    label="Are you interested in allowing family or guests to contribute photos and videos through a Memory Media Vault?"
                    helperText="The Memory Media Vault is a premium enhancement available with eligible Memory Hub services. It allows organized media collection and contribution options beyond the standard Memory Hub."
                    options={memoryMediaVaultInterestOptions}
                  />
                </div>
              </FormSection>

              <FormSection id="creative-ideas" number="09" title="Creative Ideas">
                <TextareaField
                  name="featuredMemory"
                  label="Is there a special memory, person, milestone, or story you would like highlighted?"
                  helperText="Share anything meaningful you would like Kreatethedate to consider, such as a graduation journey, family legacy, wedding story, tribute to a loved one, business achievement, or community impact."
                  rows={7}
                />
                <TextareaField
                  name="creativeIdeas"
                  label="Any Creative Ideas You Would Like Kreatethedate to Consider?"
                  helperText="Share any special ideas, themes, memory concepts, tribute ideas, family traditions, design inspirations, or unique touches you would like us to consider while creating your Memory Hub."
                  rows={8}
                />
              </FormSection>

              <FormSection id="future-services" number="10" title="Future Services Interest">
                <CheckboxGroupField
                  name="futureServicesInterest"
                  label="Would you like information about additional Kreatethedate services?"
                  options={futureServicesOptions}
                />
              </FormSection>

              <FormSection id="confirmation" number="11" title="Permission & Confirmation">
                <div className="grid gap-4">
                  <CheckboxField
                    name="accurateConfirmation"
                    label="I confirm that the information provided is accurate to the best of my knowledge."
                    required
                    error={errors.accurateConfirmation}
                  />
                  <CheckboxField
                    name="contentPermission"
                    label="I confirm that I have permission to submit and use any photos, videos, logos, graphics, wording, links, or files provided for this project."
                    required
                    error={errors.contentPermission}
                  />
                  <CheckboxField
                    name="serviceUseConfirmation"
                    label="I understand Kreatethedate will build my Memory Hub based on the information submitted, and missing information may delay the project timeline."
                    required
                    error={errors.serviceUseConfirmation}
                  />
                  <CheckboxField
                    name="changeRequestAcknowledgement"
                    label="I understand that major changes requested after the Memory Hub setup has started may require additional time and/or additional fees."
                    required
                    error={errors.changeRequestAcknowledgement}
                  />
                  <TurnstilePlaceholder />
                </div>
              </FormSection>

              <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
                <div className="grid gap-4">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Memory Hub Form</p>
                    <h2 className="mt-1 font-display text-2xl font-bold text-[#050505]">Change Request Notice</h2>
                  </div>
                  <div className="grid gap-3 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
                    <p>Please review your information carefully before submitting.</p>
                    <p>
                      Kreatethedate will use the information provided to create your Memory Hub. Major changes requested
                      after setup has begun may affect the project timeline and may require additional work, revisions, or
                      fees.
                    </p>
                    <p>
                      Submitting this form helps ensure your project can be completed as efficiently and accurately as
                      possible.
                    </p>
                  </div>
                </div>
              </section>

              <div className="flex flex-col gap-3 rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-semibold leading-7 text-[#5c5246]">
                  Please review your details before submitting your Memory Hub form.
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-6 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a] disabled:opacity-60"
                >
                  {isSubmitting ? "Submitting..." : "Submit Memory Hub Form"}
                  <Send className="size-4" aria-hidden="true" />
                </button>
              </div>
            </form>
          </div>
        </section>
      )}
    </main>
  );
}

function validateMemoryHubForm(formData: FormData) {
  const nextErrors: FormErrors = {};

  for (const name of requiredTextFields) {
    const value = String(formData.get(name) || "").trim();
    if (!value) nextErrors[name] = requiredLabels[name];
  }

  for (const name of requiredRadioFields) {
    const value = String(formData.get(name) || "").trim();
    if (!value) nextErrors[name] = requiredLabels[name];
  }

  if (String(formData.get("memoryHubFor") || "").trim() === "Other" && !String(formData.get("memoryHubForOther") || "").trim()) {
    nextErrors.memoryHubForOther = requiredLabels.memoryHubForOther;
  }

  if (String(formData.get("memoryHubType") || "").trim() === "Other" && !String(formData.get("memoryHubTypeOther") || "").trim()) {
    nextErrors.memoryHubTypeOther = requiredLabels.memoryHubTypeOther;
  }

  const email = String(formData.get("email") || "").trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    nextErrors.email = "Enter a valid email address.";
  }

  if (!hasUploadedMemoryMedia(formData)) {
    nextErrors.memoryHubPhotos = requiredLabels.memoryHubPhotos;
  }

  for (const name of requiredCheckboxFields) {
    if (!formData.get(name)) nextErrors[name] = requiredLabels[name];
  }

  return nextErrors;
}

function hasUploadedMemoryMedia(formData: FormData) {
  return ["memoryHubPhotos", "memoryHubVideos"].some((fieldName) =>
    formData.getAll(fieldName).some((entry) => entry instanceof File && entry.size > 0),
  );
}

function ThankYouState({
  onDownloadPdf,
  onPrint,
  summary,
}: {
  onDownloadPdf: () => void;
  onPrint: () => void;
  summary: SubmissionSummary | null;
}) {
  return (
    <section className="px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-5xl gap-6">
        <div className="print-hide rounded-lg border border-[#D94A16]/24 bg-white p-6 text-center shadow-[0_24px_70px_rgba(45,32,8,.12)] sm:p-8">
          <span className="mx-auto grid size-16 place-items-center rounded-full bg-[#050505] text-[#FF6A2B]">
            <CheckCircle2 className="size-9" aria-hidden="true" />
          </span>
          <h2 className="mt-6 font-display text-4xl font-bold text-[#050505]">Thank you.</h2>
          <p className="mx-auto mt-4 max-w-3xl text-lg leading-8 text-[#5c5246]">
            Your Kreatethedate Memory Hub Form has been received. We will review your details and follow up with next
            steps.
          </p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onPrint}
              className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a]"
            >
              <Printer className="size-4" aria-hidden="true" />
              Print My Form
            </button>
            <button
              type="button"
              onClick={onDownloadPdf}
              className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-[#D94A16]/35 bg-[#fff9ef] px-5 text-sm font-extrabold text-[#050505] transition hover:bg-white"
            >
              <Download className="size-4" aria-hidden="true" />
              Download My Form as PDF
            </button>
            <Link
              to="/"
              className="focus-ring inline-flex min-h-12 items-center justify-center rounded-lg border border-[#D94A16]/30 px-5 text-sm font-extrabold text-[#5c5246] transition hover:bg-[#fff9ef]"
            >
              Back to Forms Portal
            </Link>
          </div>
        </div>

        {summary ? <SubmittedFormSummary summary={summary} /> : null}
      </div>
    </section>
  );
}

function SubmittedFormSummary({ summary }: { summary: SubmissionSummary }) {
  return (
    <article className="print-document rounded-lg border border-[#D94A16]/24 bg-white p-6 shadow-[0_24px_70px_rgba(45,32,8,.1)] sm:p-8">
      <header className="border-b border-[#D94A16]/22 pb-5">
        <p className="text-xs font-black uppercase tracking-[0.22em] text-[#D94A16]">{summary.businessName}</p>
        <h2 className="mt-2 font-display text-4xl font-bold text-[#050505]">{summary.formTitle}</h2>
        <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
          <SummaryMeta label="Client name" value={summary.clientName} />
          <SummaryMeta label="Event name" value={summary.eventName} />
          <SummaryMeta label="Event date" value={summary.eventDate} />
          <SummaryMeta label="Date submitted" value={summary.submittedAt} />
        </dl>
      </header>

      <div className="mt-6 grid gap-6">
        {summary.sections.map((section) => (
          <section key={section.title} className="break-inside-avoid rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
            <h3 className="font-display text-2xl font-bold text-[#050505]">{section.title}</h3>
            <dl className="mt-4 grid gap-3">
              {section.answers.map((answer) => (
                <div key={`${section.title}-${answer.label}`} className="grid gap-1 border-t border-[#D94A16]/14 pt-3">
                  <dt className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{answer.label}</dt>
                  <dd className="whitespace-pre-wrap text-sm font-semibold leading-7 text-[#332a1f]">{answer.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </article>
  );
}

function SummaryMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3">
      <dt className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{label}</dt>
      <dd className="mt-1 font-bold leading-6 text-[#16120a]">{value}</dd>
    </div>
  );
}

function printSubmittedForm(summary: SubmissionSummary | null) {
  const previousTitle = document.title;
  if (summary) document.title = `${summary.formTitle} - ${summary.clientName}`;
  window.print();
  window.setTimeout(() => {
    document.title = previousTitle;
  }, 500);
}

function FormSection({ id, number, title, children }: { id: string; number: string; title: string; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`} className="scroll-mt-28 rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
      <div className="mb-5 flex items-start gap-4 border-b border-[#D94A16]/16 pb-4">
        <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[#050505] text-sm font-black text-[#FF6A2B]">
          {number}
        </span>
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Memory Hub Form</p>
          <h2 id={`${id}-heading`} className="mt-1 font-display text-2xl font-bold text-[#050505]">
            {title}
          </h2>
        </div>
      </div>
      {children}
    </section>
  );
}

interface BaseFieldProps {
  name: string;
  label: string;
  required?: boolean;
  error?: string;
  className?: string;
  helperText?: string;
}

interface TextFieldProps extends BaseFieldProps {
  type?: string;
  autoComplete?: string;
}

function TextField({ name, label, required, error, className = "", type = "text", autoComplete }: TextFieldProps) {
  return (
    <div className={className}>
      <FieldLabel name={name} label={label} required={required} />
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        autoComplete={autoComplete}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${name}-error` : undefined}
        className="form-field mt-2 min-h-12 w-full rounded-lg px-4 py-3 text-base focus:outline-none"
      />
      <FieldError name={name} error={error} />
    </div>
  );
}

function TextareaField({ name, label, required, error, className = "", helperText, rows = 3 }: BaseFieldProps & { rows?: number }) {
  const describedBy = [helperText ? `${name}-helper` : "", error ? `${name}-error` : ""].filter(Boolean).join(" ") || undefined;
  const fieldSpacing = helperText ? "mt-3" : "mt-2";

  return (
    <div className={className}>
      <FieldLabel name={name} label={label} required={required} />
      {helperText ? (
        <p id={`${name}-helper`} className="mt-2 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
          {helperText}
        </p>
      ) : null}
      <textarea
        id={name}
        name={name}
        rows={rows}
        required={required}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={describedBy}
        className={`form-field ${fieldSpacing} w-full rounded-lg px-4 py-3 text-base leading-7 focus:outline-none`}
      />
      <FieldError name={name} error={error} />
    </div>
  );
}

function SelectField({ name, label, required, error, className = "", options }: BaseFieldProps & { options: string[] }) {
  return (
    <div className={className}>
      <FieldLabel name={name} label={label} required={required} />
      <select
        id={name}
        name={name}
        required={required}
        defaultValue=""
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${name}-error` : undefined}
        className="form-field mt-2 min-h-12 w-full rounded-lg px-4 py-3 text-base focus:outline-none"
      >
        <option value="" disabled>
          Select one
        </option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <FieldError name={name} error={error} />
    </div>
  );
}

function FileField({ name, label, className = "", accept, multiple, helperText, required, error }: BaseFieldProps & { accept?: string; multiple?: boolean }) {
  const describedBy = [helperText ? `${name}-helper` : "", error ? `${name}-error` : ""].filter(Boolean).join(" ") || undefined;
  const fieldSpacing = helperText ? "mt-3" : "mt-2";

  return (
    <div className={className}>
      <FieldLabel name={name} label={label} required={required} />
      {helperText ? (
        <p id={`${name}-helper`} className="mt-2 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
          {helperText}
        </p>
      ) : null}
      <label
        className={`${fieldSpacing} flex min-h-24 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-5 text-center text-sm font-bold text-[#5c5246] transition hover:border-[#D94A16]/60 hover:bg-white ${
          error ? "border-red-400 bg-red-50" : "border-[#D94A16]/36 bg-[#fff9ef]"
        }`}
      >
        <UploadCloud className="size-6 text-[#D94A16]" aria-hidden="true" />
        Choose file{multiple ? "s" : ""}
        <input
          id={name}
          name={name}
          type="file"
          accept={accept}
          multiple={multiple}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={describedBy}
          className="sr-only"
        />
      </label>
      <FieldError name={name} error={error} />
    </div>
  );
}

function RadioGroupField({
  name,
  label,
  options,
  required,
  error,
  helperText,
  className = "",
  onChange,
}: BaseFieldProps & { options: string[]; onChange?: (value: string) => void }) {
  const describedBy = [helperText ? `${name}-helper` : "", error ? `${name}-error` : ""].filter(Boolean).join(" ") || undefined;
  const optionSpacing = helperText ? "mt-3" : "mt-3";

  return (
    <fieldset className={className} aria-invalid={error ? "true" : "false"} aria-describedby={describedBy}>
      <legend className="text-sm font-black text-[#16120a]">
        {label} {required ? <RequiredMark /> : null}
      </legend>
      {helperText ? (
        <p id={`${name}-helper`} className="mt-2 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
          {helperText}
        </p>
      ) : null}
      <div className={`${optionSpacing} grid gap-3 sm:grid-cols-2`}>
        {options.map((option) => (
          <label
            key={option}
            className={`flex min-h-12 items-start gap-3 rounded-lg border bg-[#fffdf8] p-4 text-sm font-semibold leading-7 text-[#4f4538] ${
              error ? "border-red-400" : "border-[#D94A16]/22"
            }`}
          >
            <input
              name={name}
              type="radio"
              value={option}
              required={required}
              className="mt-1 size-5 shrink-0 accent-[#050505]"
              onChange={(event) => {
                if (event.currentTarget.checked) onChange?.(option);
              }}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
      <FieldError name={name} error={error} />
    </fieldset>
  );
}

function CheckboxGroupField({
  name,
  label,
  options,
  className = "",
  onChange,
}: BaseFieldProps & { options: string[]; onChange?: (option: string, checked: boolean) => void }) {
  return (
    <fieldset className={className}>
      <legend className="text-sm font-black text-[#16120a]">{label}</legend>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {options.map((option) => (
          <label
            key={option}
            className="flex min-h-12 items-start gap-3 rounded-lg border border-[#D94A16]/22 bg-[#fffdf8] p-4 text-sm font-semibold leading-7 text-[#4f4538]"
          >
            <input
              name={name}
              type="checkbox"
              value={option}
              className="mt-1 size-5 shrink-0 accent-[#050505]"
              onChange={(event) => onChange?.(option, event.currentTarget.checked)}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function CheckboxField({ name, label, required, error }: BaseFieldProps) {
  return (
    <div>
      <label
        className={`flex gap-3 rounded-lg border bg-[#fffdf8] p-4 text-sm font-semibold leading-7 text-[#4f4538] ${
          error ? "border-red-400" : "border-[#D94A16]/22"
        }`}
      >
        <input
          id={name}
          name={name}
          type="checkbox"
          required={required}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={error ? `${name}-error` : undefined}
          className="mt-1 size-5 shrink-0 accent-[#050505]"
        />
        <span>
          {label} {required ? <RequiredMark /> : null}
        </span>
      </label>
      <FieldError name={name} error={error} />
    </div>
  );
}

function TurnstilePlaceholder() {
  return (
    <div className="rounded-lg border border-[#D94A16]/22 bg-[#fff9ef] p-4">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[#050505] text-[#FF6A2B]">
          <ShieldCheck className="size-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-sm font-black text-[#050505]">Security verification</p>
          <p className="mt-1 text-sm font-semibold leading-6 text-[#5c5246]">
            Cloudflare Turnstile verification will appear here when connected.
          </p>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ name, label, required }: { name: string; label: string; required?: boolean }) {
  return (
    <label htmlFor={name} className="text-sm font-black text-[#16120a]">
      {label} {required ? <RequiredMark /> : null}
    </label>
  );
}

function RequiredMark() {
  return (
    <>
      <span className="font-black text-[#D94A16]" aria-hidden="true">
        *
      </span>
      <span className="sr-only"> required</span>
    </>
  );
}

function FieldError({ name, error }: { name: string; error?: string }) {
  return error ? (
    <p id={`${name}-error`} className="mt-2 text-sm font-bold text-red-700" role="alert">
      {error}
    </p>
  ) : null;
}
